import React, { useState, useMemo } from "react";
import { updateMatchResult } from "services/matchService";
import * as Sentry from "@sentry/react";
import ScoreEditModal from "./scoreEditModal";

const TournamentMatchesTable = ({ matches, setMatches, tournament }) => {
  const [editingScores, setEditingScores] = useState({});
  const [editingMatchId, setEditingMatchId] = useState(null);
  const [searchTerms, setSearchTerms] = useState({});
  const [resultFilters, setResultFilters] = useState({});

  const hasCrosspoolMatches = useMemo(() =>
    matches?.some((m) => m.phase === "crosspool"), [matches]
  );

  const normalizeText = (text) =>
    text
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase();

  const getTeamDisplayName = (match, teamKey) => {
    if (match[teamKey]?.players) {
      return match[teamKey].players.map((p) => p.name).join(" & ");
    }
    return match[`${teamKey}Name`] || "BYE";
  };

  const matchesByDivision = useMemo(() => {
    const grouped = {};

    matches
      .filter((m) => m.phase !== "elimination") // 🔥 Only pool phase matches
      .forEach((match) => {
        const division = match.division || "merged";
        if (!grouped[division]) {
          grouped[division] = [];
        }
        grouped[division].push(match);
      });

    return grouped;
  }, [matches]);

  const handleScoreChange = (matchId, teamKey, value) => {
    if (tournament?.compiled) {
      alert(
        "❌ Ce tournoi a déjà été compilé. Vous ne pouvez plus modifier les scores."
      );
      return;
    }
    setEditingScores((prev) => {
      const current = prev[matchId] || {
        team1: matches.find((m) => m.id === matchId)?.result?.team1 ?? 0,
        team2: matches.find((m) => m.id === matchId)?.result?.team2 ?? 0,
      };
      return {
        ...prev,
        [matchId]: {
          ...current,
          [teamKey]: Math.max(0, parseInt(value, 10) || 0),
        },
      };
    });
  };

  const handleScoreSubmit = async (matchId) => {
    const fallbackMatch = matches.find((m) => m.id === matchId);
    const result = {
      team1:
        editingScores[matchId]?.team1 ?? fallbackMatch?.result?.team1 ?? "",
      team2:
        editingScores[matchId]?.team2 ?? fallbackMatch?.result?.team2 ?? "",
    };

    if (!isNaN(result.team1) && !isNaN(result.team2)) {
      try {
        const newResult =
          result.team1 === 0 && result.team2 === 0 ? null : result;
        await updateMatchResult(matchId, newResult);

        setMatches((prev) =>
          prev.map((m) => (m.id === matchId ? { ...m, result: newResult } : m))
        );

        setEditingMatchId(null);
        setEditingScores((prev) => {
          const updated = { ...prev };
          delete updated[matchId];
          return updated;
        });
      } catch (error) {
        Sentry.captureException(error);
        console.error("Erreur lors de la mise à jour du score :", error);
        alert("❌ Impossible d'enregistrer le score.");
      }
    } else {
      alert("⚠️ Veuillez saisir un score valide pour les deux équipes.");
    }
  };

  if (!matches?.length) return <p>Aucun match pour ce tournoi.</p>;

  return (
    <div className="mt-6 w-full flex flex-wrap justify-center gap-8">
      {Object.keys(matchesByDivision)
        .sort()
        .map((divisionKey) => {
          const matches = matchesByDivision[divisionKey];

          const search = normalizeText(searchTerms[divisionKey] || "");
          const filter = resultFilters[divisionKey] || "all";

          const filteredMatches = matches.filter((match) => {
            const names = [
              normalizeText(getTeamDisplayName(match, "team1")),
              normalizeText(getTeamDisplayName(match, "team2")),
            ];
            const matchesSearch =
              !search || names.some((name) => name.includes(search));

            const hasResult = !!match.result;
            const matchesResultFilter =
              filter === "all" ||
              (filter === "with" && hasResult) ||
              (filter === "without" && !hasResult);

            return matchesSearch && matchesResultFilter;
          });

          if (!filteredMatches.length) return null;

          return (
            <div
              key={divisionKey}
              className="w-full md:w-[48%] overflow-x-auto bg-slate-800 border border-slate-700 p-4 rounded-lg shadow-lg text-slate-50"
            >
              <h3 className="text-xl font-bold mb-4 text-start">
                {divisionKey === "merged"
                  ? "Général"
                  : divisionKey === "crosspool"
                  ? "Matchs Inter-Groupes"
                  : divisionKey === "division1"
                  ? (!tournament?.makeDivisions ? "Groupe 1" : "Division 1")
                  : (!tournament?.makeDivisions ? "Groupe 2" : "Division 2")}
              </h3>

              <div className="flex items-center gap-2 mb-4">
                <input
                  type="text"
                  placeholder="Rechercher équipe..."
                  className="border rounded px-3 py-2 flex-1"
                  value={searchTerms[divisionKey] || ""}
                  onChange={(e) =>
                    setSearchTerms((prev) => ({
                      ...prev,
                      [divisionKey]: e.target.value,
                    }))
                  }
                />
                <select
                  className="border rounded px-2 py-2"
                  value={resultFilters[divisionKey] || "all"}
                  onChange={(e) =>
                    setResultFilters((prev) => ({
                      ...prev,
                      [divisionKey]: e.target.value,
                    }))
                  }
                >
                  <option value="all">Tous</option>
                  <option value="with">Avec score</option>
                  <option value="without">Sans score</option>
                </select>
              </div>

              <table className="min-w-full table-auto text-xs md:text-sm text-slate-300">
                <thead className="bg-slate-800 text-slate-50">
                  <tr>
                    <th className="px-2 py-1 md:px-2 md:py-2">#</th>
                    <th className="px-2 py-1 md:px-4 md:py-2">Home</th>
                    <th className="px-2 py-1 md:px-4 md:py-2">Guest</th>
                    <th className="px-2 py-1 md:px-4 md:py-2">Résultat</th>
                    <th className="px-2 py-1 md:px-4 md:py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMatches.map((match, idx) => (
                    <tr key={match.id} className="bg-slate-800 border-b">
                      <td className="px-2 py-1 text-center">{idx + 1}</td>
                      <td className="px-2 py-1">
                        {getTeamDisplayName(match, "team1")}
                      </td>
                      <td className="px-2 py-1">
                        {getTeamDisplayName(match, "team2")}
                      </td>
                      <td className="px-2 py-1 text-center">
                        {match.result
                          ? `${match.result.team1} - ${match.result.team2}`
                          : "—"}
                      </td>
                      <td className="px-2 py-1 text-center">
                        <button
                          onClick={() => setEditingMatchId(match.id)}
                          disabled={tournament?.compiled}
                          className={`text-sm ${
                            tournament?.compiled
                              ? "bg-slate-700 cursor-not-allowed"
                              : "bg-yellow-500 hover:bg-yellow-600"
                          } text-white px-2 py-1 rounded`}
                        >
                          Modifier
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}

      {/* Modal */}
      {editingMatchId && (
        <ScoreEditModal
          matchId={editingMatchId}
          score={
            editingScores[editingMatchId] || {
              team1:
                matches.find((m) => m.id === editingMatchId)?.result?.team1 ??
                "",
              team2:
                matches.find((m) => m.id === editingMatchId)?.result?.team2 ??
                "",
            }
          }
          onChange={handleScoreChange}
          onCancel={() => setEditingMatchId(null)}
          onSave={handleScoreSubmit}
          team1Name={getTeamDisplayName(
            matches.find((m) => m.id === editingMatchId),
            "team1"
          )}
          team2Name={getTeamDisplayName(
            matches.find((m) => m.id === editingMatchId),
            "team2"
          )}
          goalsToWin={matches.find((m) => m.id === editingMatchId)?.goalsToWin || 1}
        />
      )}
    </div>
  );
};

export default TournamentMatchesTable;
